/**
 * DOM utility helpers for Shadow DOM rendering.
 */

/** Create an element with optional attributes and children */
export function h(
  tag: string,
  attrs?: Record<string, string | boolean | EventListener> | null,
  ...children: (string | Node | null | undefined)[]
): HTMLElement {
  const el = document.createElement(tag);

  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (key.startsWith('on') && typeof value === 'function') {
        const eventName = key.slice(2).toLowerCase();
        el.addEventListener(eventName, value as EventListener);
      } else if (typeof value === 'boolean') {
        if (value) el.setAttribute(key, '');
      } else if (typeof value === 'string') {
        el.setAttribute(key, value);
      }
    }
  }

  for (const child of children) {
    if (child == null) continue;
    if (typeof child === 'string') {
      el.appendChild(document.createTextNode(child));
    } else {
      el.appendChild(child);
    }
  }

  return el;
}

/** Safely set innerHTML with basic XSS prevention */
export function sanitizeText(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/** Format a timestamp into a readable time string */
export function formatTime(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Generate a unique ID */
export function uid(): string {
  return Math.random().toString(36).substring(2, 10);
}
